import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ConfirmDialog } from '../../components/Feedback';
import { LocalDataTable } from '../../components/LocalDataTable';
import { FormGrid, NumberField, TextField, useForm } from '../../components/Form';
import { api } from '../../lib/api';
import { useMutation, useResource } from '../../lib/hooks';

type EnrollCourseRow = {
  courseId: number;
  grade: string | null;
  subject: string | null;
  groupName: string | null;
  teacher: string | null;
  isSelected: boolean;
};

type EnrollView = {
  studentId: number;
  studentName: string;
  courses: EnrollCourseRow[];
};

/**
 * Course enrolment.
 *
 * The whole course list is posted back with its tick states, not just the changes. That
 * shape matters: re-ticking a course the student was previously enrolled on reuses the
 * existing StudentCourse row, which keeps whatever fee was negotiated for them. Sending
 * only the additions would create a fresh row at the course default and silently wipe out
 * a discount.
 */
export function EnrollmentPage() {
  const { studentId } = useParams();
  const navigate = useNavigate();
  const { run, busy } = useMutation();

  const view = useResource<EnrollView>(studentId ? `/api/enrollment/${studentId}` : null);
  const [selected, setSelected] = useState<Record<number, boolean>>({});
  const [filter, setFilter] = useState('');

  useEffect(() => {
    if (!view.data) return;
    setSelected(Object.fromEntries(view.data.courses.map((c) => [c.courseId, c.isSelected])));
  }, [view.data]);

  const courses = (view.data?.courses ?? []).filter((course) => {
    if (!filter.trim()) return true;
    const term = filter.toLowerCase();
    return [course.grade, course.subject, course.groupName, course.teacher]
      .some((field) => field?.toLowerCase().includes(term));
  });

  const selectedCount = Object.values(selected).filter(Boolean).length;

  async function save() {
    if (!view.data) return;

    const ok = await run(
      () => api.post('/api/enrollment', {
        studentId: view.data!.studentId,
        // Every course, with its state - not just the ticked ones.
        courses: view.data!.courses.map((c) => ({
          courseId: c.courseId,
          isSelected: !!selected[c.courseId],
        })),
      }),
      'Enrolment updated.',
    );

    if (ok) navigate(`/fees/change/${view.data.studentId}`);
  }

  return (
    <>
      <div className="page__header">
        <div>
          <h2 className="page__title">
            Enrol courses{view.data ? ` — ${view.data.studentName}` : ''}
          </h2>
          <p className="page__subtitle">
            Re-ticking a previously dropped course keeps the fee that was agreed for this student.
          </p>
        </div>
        <span className="badge badge--paid">{selectedCount} selected</span>
      </div>

      <div className="card">
        {view.loading && <p>Loading…</p>}
        {view.error && <p className="field__error">{view.error}</p>}

        {view.data && (
          <>
            <input
              className="input"
              style={{ maxWidth: 320, marginBottom: 16 }}
              placeholder="Filter by grade, subject, group or teacher"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              aria-label="Filter courses"
            />

            {/* Paged for display only. `selected` holds every course, and the save posts
                the full list back, so ticks on other pages are never lost. */}
            <LocalDataTable
              rows={courses}
              rowKey={(r) => r.courseId}
              emptyMessage="No courses match that filter."
              columns={[
                {
                  key: 'enrol', header: 'Enrol', width: '70px',
                  render: (course) => (
                    <input
                      type="checkbox"
                      checked={!!selected[course.courseId]}
                      onChange={(e) =>
                        setSelected((current) => ({ ...current, [course.courseId]: e.target.checked }))
                      }
                      aria-label={`Enrol on course C-${course.courseId}`}
                    />
                  ),
                },
                { key: 'grade', header: 'Grade', render: (r) => r.grade ?? '—' },
                { key: 'subject', header: 'Subject', render: (r) => r.subject ?? '—' },
                { key: 'group', header: 'Group', render: (r) => r.groupName ?? '—' },
                { key: 'teacher', header: 'Teacher', render: (r) => r.teacher ?? '—' },
              ]}
            />

            <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
              <button type="button" className="btn btn--primary" onClick={() => void save()} disabled={busy}>
                {busy ? 'Saving…' : 'Save enrolment'}
              </button>
              <button type="button" className="btn" onClick={() => navigate('/students')} disabled={busy}>
                Cancel
              </button>
            </div>
          </>
        )}
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------
// Course schedules
// ---------------------------------------------------------------------------

type CourseSchedule = {
  courseScheduleId: number;
  courseId: number;
  roomNo: number | '';
  day: string;
  startTime: string;
  endTime: string;
  duration: string;
};

const emptySchedule: CourseSchedule = {
  courseScheduleId: 0, courseId: 0, roomNo: '', day: '', startTime: '', endTime: '', duration: '',
};

/**
 * Weekly schedule for a course.
 *
 * Schedules are the one thing in this system that is genuinely deleted rather than flagged,
 * because unlike fees and enrolments they carry no history worth keeping.
 */
export function CourseSchedulesPage() {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const { run, busy } = useMutation();

  const schedules = useResource<CourseSchedule[]>(courseId ? `/api/courses/${courseId}/schedules` : null);
  const form = useForm<CourseSchedule>({ ...emptySchedule, courseId: Number(courseId) || 0 });
  const [editing, setEditing] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<CourseSchedule | null>(null);

  const v = form.values;

  function startNew() {
    form.setValues({ ...emptySchedule, courseId: Number(courseId) || 0 });
    setEditing(true);
  }

  function startEdit(schedule: CourseSchedule) {
    form.setValues(schedule);
    setEditing(true);
  }

  /** Day, Start Time, End Time and Duration are all [Required] on CourseSchedule. */
  function validate(): Record<string, string> {
    const e: Record<string, string> = {};
    if (!v.day.trim()) e.day = 'Day is required.';
    if (!v.startTime.trim()) e.startTime = 'Start time is required.';
    if (!v.endTime.trim()) e.endTime = 'End time is required.';
    if (!v.duration.trim()) e.duration = 'Duration is required.';
    return e;
  }

  async function save() {
    const problems = validate();
    if (Object.keys(problems).length > 0) {
      form.setErrors(problems);
      document.getElementById(Object.keys(problems)[0])?.focus();
      return;
    }
    form.setErrors({});

    const ok = await run(
      () => (v.courseScheduleId
        ? api.put('/api/courses/schedules', v)
        : api.post('/api/courses/schedules', v)),
      v.courseScheduleId ? 'Schedule updated.' : 'Schedule added.',
      () => schedules.reload(),
      (fieldErrors) => {
        form.setErrors(fieldErrors);
        document.getElementById(Object.keys(fieldErrors)[0] ?? '')?.focus();
      },
    );

    if (ok) setEditing(false);
  }

  async function confirmDelete() {
    if (!pendingDelete) return;

    const ok = await run(
      () => api.delete(`/api/courses/schedules/${pendingDelete.courseScheduleId}`),
      'Schedule removed.',
      () => schedules.reload(),
    );

    if (ok) setPendingDelete(null);
  }

  return (
    <>
      <div className="page__header">
        <div>
          <h2 className="page__title">Schedule — course C-{courseId}</h2>
          <p className="page__subtitle">Weekly class times and rooms.</p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button type="button" className="btn btn--accent" onClick={startNew}>Add slot</button>
          <button type="button" className="btn" onClick={() => navigate('/courses')}>Back</button>
        </div>
      </div>

      {editing && (
        <div className="card" style={{ marginBottom: 24 }}>
          <h3 className="card__title">{v.courseScheduleId ? 'Edit slot' : 'New slot'}</h3>
          <FormGrid>
            <TextField id="day" label="Day" required value={v.day} onChange={form.setText('day')} placeholder="Monday" error={form.errors.day} />
            {/* RoomNo is an int in the schema, so it must be sent as a number. */}
            <NumberField id="roomNo" label="Room" value={v.roomNo} onChange={form.setNumber('roomNo')} />
            <TextField id="startTime" label="Start time" required value={v.startTime} onChange={form.setText('startTime')} placeholder="16:00" error={form.errors.startTime} />
            <TextField id="endTime" label="End time" required value={v.endTime} onChange={form.setText('endTime')} placeholder="17:30" error={form.errors.endTime} />
            <TextField id="duration" label="Duration" required value={v.duration} onChange={form.setText('duration')} placeholder="90 min" error={form.errors.duration} />
          </FormGrid>
          <div style={{ display: 'flex', gap: 8 }}>
            <button type="button" className="btn btn--primary" onClick={() => void save()} disabled={busy}>
              {busy ? 'Saving…' : 'Save slot'}
            </button>
            <button type="button" className="btn" onClick={() => setEditing(false)} disabled={busy}>Cancel</button>
          </div>
        </div>
      )}

      <LocalDataTable
        rows={schedules.data ?? []}
        loading={schedules.loading}
        error={schedules.error}
        rowKey={(r) => r.courseScheduleId}
        emptyMessage="No slots scheduled."
        columns={[
          { key: 'day', header: 'Day', render: (r) => r.day },
          { key: 'room', header: 'Room', render: (r) => r.roomNo },
          { key: 'start', header: 'Start', render: (r) => r.startTime },
          { key: 'end', header: 'End', render: (r) => r.endTime },
          { key: 'duration', header: 'Duration', render: (r) => r.duration },
          {
            key: 'actions', header: '', align: 'right',
            render: (schedule) => (
              <span style={{ display: 'inline-flex', gap: 6 }}>
                <button type="button" className="btn btn--sm" onClick={() => startEdit(schedule)}>Edit</button>
                <button type="button" className="btn btn--sm btn--danger" onClick={() => setPendingDelete(schedule)}>Delete</button>
              </span>
            ),
          },
        ]}
      />

      <ConfirmDialog
        open={pendingDelete !== null}
        destructive
        busy={busy}
        title="Delete this schedule slot?"
        confirmLabel="Delete"
        message={pendingDelete
          ? <p>{pendingDelete.day} {pendingDelete.startTime}–{pendingDelete.endTime}, room {pendingDelete.roomNo}. This is a permanent delete.</p>
          : null}
        onConfirm={() => void confirmDelete()}
        onCancel={() => setPendingDelete(null)}
      />
    </>
  );
}
